import {
  NewTransaction,
  Transaction,
  TransactionsPage,
  TransactionsSummary,
  TransactionType,
  UpdateTransaction,
} from '../../models/transactions';
import AccountingApiClient from './base-client';

export interface PageRequest {
  page: number;
  perPage: number;
  filter: {
    dateFrom?: string;
    dateTo?: string;
    categoryId?: string;
    accountId?: string;
    type?: TransactionType;
    description?: string;
  };
}

class TransactionsApi extends AccountingApiClient<
  Transaction,
  TransactionsPage
> {
  public constructor() {
    super('/transactions');
  }

  private buildFilter = (filter: PageRequest['filter']): string | undefined => {
    if (!filter) {
      return;
    }

    const expressions = [];

    if (filter.type) {
      expressions.push(`type eq '${filter.type}'`);
    }

    if (filter.categoryId) {
      expressions.push(`category eq '${filter.categoryId}'`);
    }

    if (filter.dateFrom) {
      expressions.push(`date gte '${filter.dateFrom}'`);
    }

    if (filter.accountId) {
      expressions.push(
        `(account eq '${filter.accountId}' or otherAccount eq '${filter.accountId}')`
      );
    }

    if (filter.dateTo) {
      expressions.push(`date lte '${filter.dateTo}'`);
    }

    if (filter.description) {
      expressions.push(`contains(description, '${filter.description}')`);
    }

    return expressions.join(' and ');
  };

  public getPage = async ({
    page,
    perPage,
    filter,
  }: PageRequest): Promise<TransactionsPage> => {
    let url = `${this.baseUrl}?`;

    if (page) {
      url = `${url}&page=${page}`;
    }
    if (perPage) {
      url = `${url}&per_page=${perPage}`;
    }

    const filterExpression = this.buildFilter(filter);

    if (filterExpression) {
      url = `${url}&filter=${filterExpression}`;
    }

    return super.httpList(url);
  };

  public getSummary = async (
    filter: PageRequest['filter']
  ): Promise<TransactionsSummary> => {
    let url = `${this.baseUrl}/summary?`;

    const filterExpression = this.buildFilter(filter);
    if (filterExpression) {
      url = `${url}&filter=${filterExpression}`;
    }

    return this.fetch<TransactionsSummary>(url, { method: 'GET' });
  };

  public get = async (id: string): Promise<Transaction> => {
    return super.httpGet(`${this.baseUrl}/${id}`);
  };

  public create = async (transaction: NewTransaction): Promise<Transaction> => {
    return super.httpPost<NewTransaction>(this.baseUrl, transaction);
  };

  public update = async (
    transaction: UpdateTransaction
  ): Promise<Transaction> => {
    const { id, ...body } = transaction;
    return super.httpPut<NewTransaction>(`${this.baseUrl}/${id}`, body);
  };

  public delete = async (id: string): Promise<void> => {
    return super.httpDelete(`${this.baseUrl}/${id}`);
  };
}

const transactionsApi = new TransactionsApi();

export default transactionsApi;
